import {
  FindingSchema,
  ReportFileSchema as PrismaReportFileSchema,
  ReportSchema,
} from '@terra/shared-schemas-orchestrator';
import { uploadedFilesSchema } from '@terra/shared-authorization';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import { z } from 'zod';
import {
  BulkFilterTooManyMatchesResponseSchema,
  BulkFindingsFilterSchema,
} from '@terra/shared-contracts-findings';

export const ReportsIdSchema = z.object({
  id: z.string(),
});

export const REPORTS_LIST_MAX_PAGE_SIZE = 1000;

export const ReportsListPageQuerySchema = z.object({
  skip: z.coerce.number().int().min(0).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(REPORTS_LIST_MAX_PAGE_SIZE)
    .optional(),
});

export const ReportFileIdSchema = ReportsIdSchema.extend({
  fileId: z.string(),
});

export const ApplicationIdSchema = z.object({
  applicationId: z.string(),
});

export const ReportSourceSchema = z.enum(['MANUAL', 'SCHEDULED', 'ON_DEMAND']);

export const OnDemandReportTemplateSchema = z.enum([
  'SECURITY_REPORT',
  'LETTER_OF_ATTESTATION',
  'EXECUTIVE_SUMMARY',
]);

export const OnDemandReportTimeframePresetSchema = z.enum([
  'LAST_7_DAYS',
  'LAST_30_DAYS',
  'LAST_12_MONTHS',
  'ALL_TIME',
  'CUSTOM',
]);

export const OnDemandReportTimeframeSchema = z
  .object({
    preset: OnDemandReportTimeframePresetSchema,
    createdStartDate: z.coerce.date().optional(),
    createdEndDate: z.coerce.date().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.preset !== 'CUSTOM') {
      return;
    }

    if (!value.createdStartDate || !value.createdEndDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Custom timeframe requires createdStartDate and createdEndDate',
        path: ['createdStartDate'],
      });

      return;
    }

    if (value.createdStartDate.getTime() > value.createdEndDate.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Custom timeframe end date must be on or after the start date',
        path: ['createdEndDate'],
      });
    }
  });

export const OnDemandReportOptionsSchema = z.object({
  includeScreenshots: z.boolean().default(true),
  includeRemediation: z.boolean().default(false),
  includeFindingsHighLevelOverview: z.boolean().default(false),
  includeResolvedFindings: z.boolean().default(false),
});

export const OnDemandReportGenerationScopeSchema = z.object({
  timeframePreset: OnDemandReportTimeframePresetSchema,
  includeResolvedFindings: z.boolean(),
});

export const OnDemandReportBodySchema = z
  .object({
    template: z.preprocess(
      (value) =>
        value === 'TERRA_SECURITY_REPORT'
          ? OnDemandReportTemplateSchema.enum.SECURITY_REPORT
          : value,
      OnDemandReportTemplateSchema,
    ),
    timeframe: OnDemandReportTimeframeSchema,
    options: OnDemandReportOptionsSchema.optional(),
    applicationIds: z
      .array(MongoObjectIdSchema)
      .optional()
      .describe(
        'Applications to include for Executive Summary. Required and non-empty when template is EXECUTIVE_SUMMARY.',
      ),
    title: z
      .string()
      .optional()
      .describe('Optional custom title for the report'),
    jobId: z
      .string()
      .optional()
      .describe('Job ID for tracking report generation progress via SSE'),
  })
  .superRefine((value, ctx) => {
    if (value.template !== 'EXECUTIVE_SUMMARY') {
      return;
    }

    if (!value.applicationIds || value.applicationIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Select at least one application for the Executive Summary report',
        path: ['applicationIds'],
      });
    }
  });

export const OnDemandReportPreviewBodySchema = z.object({
  timeframe: OnDemandReportTimeframeSchema,
  includeResolvedFindings: z.boolean().optional().default(false),
  applicationIds: z
    .array(MongoObjectIdSchema)
    .optional()
    .describe(
      'Applications to preview for Executive Summary. When set, counts are summed across these apps.',
    ),
});

export const MultiAppOnDemandReportPreviewBodySchema =
  OnDemandReportPreviewBodySchema.extend({
    applicationIds: z
      .array(MongoObjectIdSchema)
      .min(1)
      .describe('Applications to include in the Executive Summary preview'),
  });

export const MultiAppOnDemandReportTemplateSchema = z.enum([
  'EXECUTIVE_SUMMARY',
  'LETTER_OF_ATTESTATION',
]);

export const MULTI_TARGET_SUPPORTED_ON_DEMAND_REPORT_TEMPLATES =
  MultiAppOnDemandReportTemplateSchema.options;

export const multiTargetSupportedReports =
  MULTI_TARGET_SUPPORTED_ON_DEMAND_REPORT_TEMPLATES;

export const isMultiTargetSupportedOnDemandReportTemplate = (
  template: z.infer<typeof OnDemandReportTemplateSchema>,
): template is z.infer<typeof MultiAppOnDemandReportTemplateSchema> =>
  MultiAppOnDemandReportTemplateSchema.safeParse(template).success;

export const MultiAppOnDemandReportBodySchema = z.object({
  template: MultiAppOnDemandReportTemplateSchema,
  timeframe: OnDemandReportTimeframeSchema,
  options: OnDemandReportOptionsSchema.optional(),
  applicationIds: z
    .array(MongoObjectIdSchema)
    .min(1)
    .describe('Applications to include in the Executive Summary report'),
  title: z.string().optional().describe('Optional custom title for the report'),
  jobId: z
    .string()
    .optional()
    .describe('Job ID for tracking report generation progress via SSE'),
});

export const OnDemandReportPreviewResponseSchema = z.object({
  matchedCount: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
});

export const OnDemandReportGenerateResponseSchema = z.object({
  message: z.string().describe('Status message'),
  jobId: z.string().describe('Job ID for tracking progress via SSE'),
});

export const ScheduledReportFrequencySchema = z.enum([
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'SEMI_ANNUAL',
  'YEARLY',
]);

export const ScheduledReportChangeBucketSchema = z.enum([
  'NEW',
  'RESOLVED',
  'SEVERITY_CHANGED',
  'STATUS_CHANGED',
  'UNCHANGED',
  'BASELINE',
]);

export const ScheduledReportChangesStateSchema = z.enum([
  'FIRST_RUN',
  'POPULATED',
  'NO_UPDATES',
]);

export const ScheduledReportSeverityMixSchema = z.object({
  critical: z.number().int().nonnegative(),
  high: z.number().int().nonnegative(),
  medium: z.number().int().nonnegative(),
  low: z.number().int().nonnegative(),
  info: z.number().int().nonnegative(),
});

export const ScheduledReportChangeRowSchema = z.object({
  findingId: z.string(),
  title: z.string(),
  severity: z.string(),
  status: z.string(),
  lastUpdated: z.string(),
  bucket: ScheduledReportChangeBucketSchema,
  whatChanged: z.string(),
  previousSeverity: z.string().optional(),
  previousStatus: z.string().optional(),
});

export const ChangesSinceLastScheduledReportSchema = z.object({
  state: ScheduledReportChangesStateSchema,
  windowStart: z.string().nullable(),
  windowEnd: z.string(),
  priorReportId: z.string().nullable(),
  counts: z.object({
    new: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
    severityChanged: z.number().int().nonnegative(),
    statusChanged: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    baseline: z.number().int().nonnegative(),
  }),
  severityMix: z.object({
    prior: ScheduledReportSeverityMixSchema.nullable(),
    current: ScheduledReportSeverityMixSchema,
  }),
  rows: z.array(ScheduledReportChangeRowSchema),
});

export const ReportsFindingsBodySchema = z.object({
  findingsIds: z
    .array(z.string())
    .describe('The findings ids to add to the report'),
  applicationId: z.string().describe('The application id'),
});

export const ReportsFindingsByFilterBodySchema = BulkFindingsFilterSchema;

export const ReportsTooManyMatchesResponseSchema =
  BulkFilterTooManyMatchesResponseSchema;

const ReportRelatedFindingSchema = FindingSchema.extend({
  pocBlocks: FindingSchema.shape.pocBlocks.optional(),
  pocBlocksEnc: FindingSchema.shape.pocBlocksEnc.optional(),
});

export const ReportsReturnSchema = ReportSchema.extend({
  relatedFindings: z.array(ReportRelatedFindingSchema),
  files: z.array(PrismaReportFileSchema),
});

export const ReportsBodySchema = z.object({
  findings: z.array(z.string()).describe('The findings to add to the report'),
  title: z.string().optional().describe('Optional custom title for the report'),
  nextStepsSummary: z
    .string()
    .optional()
    .nullable()
    .describe('Strategic next steps summary for the report'),
  isNewReport: z
    .boolean()
    .default(true)
    .describe('Whether to create a new report'),
  reportId: z.string().optional().describe('The report id to update'),
  jobId: z
    .string()
    .optional()
    .describe('Job ID for tracking report generation progress via SSE'),
  contactName: z
    .string()
    .optional()
    .nullable()
    .describe('Contact name for the report'),
  reportDate: z.coerce
    .date()
    .optional()
    .nullable()
    .describe('Custom report date (defaults to current date)'),
  testPeriodStart: z.coerce
    .date()
    .optional()
    .nullable()
    .describe('Start date of the testing period'),
  testPeriodEnd: z.coerce
    .date()
    .optional()
    .nullable()
    .describe('End date of the testing period'),
  exclusions: z
    .array(z.string())
    .optional()
    .describe('List of exclusions/out-of-scope items'),
  limitations: z
    .array(z.string())
    .optional()
    .describe('List of testing limitations'),
  methodologyType: z
    .enum(['white-box', 'gray-box', 'black-box'])
    .optional()
    .describe('Testing methodology type (white-box, gray-box, or black-box)'),
  source: ReportSourceSchema.optional().describe(
    'Whether the report was created manually, on demand, or by a schedule',
  ),
  cadence: ScheduledReportFrequencySchema.optional()
    .nullable()
    .describe('Cadence label for scheduled reports'),
  scheduleRuleId: z
    .string()
    .optional()
    .nullable()
    .describe('Scheduled report rule id that produced this report'),
  scheduleSlotKey: z
    .string()
    .optional()
    .nullable()
    .describe('Idempotency key for the scheduled report slot'),
  priorScheduledReportId: z
    .string()
    .optional()
    .nullable()
    .describe('Prior scheduled report used as the change baseline'),
  changesSinceLast: ChangesSinceLastScheduledReportSchema.optional()
    .nullable()
    .describe('Precomputed changes since the last scheduled report'),
  pdfTemplateId: z
    .string()
    .optional()
    .nullable()
    .describe('PDF template id used when generating the report file'),
  applicationIds: z
    .array(z.string())
    .optional()
    .describe(
      'Selected application ids for multi-application reports (e.g. Executive Summary)',
    ),
});

export const ReportDeleteSchema = z.object({
  message: z.string(),
});

export const ReportFilesQuerySchema = z.object({
  source: ReportSourceSchema.optional().describe(
    'Filter report files by report source (Manual or Scheduled)',
  ),
});

export const ReportFileSchema = z.object({
  id: z.string(),
  name: z.string(),
  size: z.number(),
  originalSize: z.number().nullable().optional(),
  mimeType: z.string().nullable().optional(),
  status: z.enum(['DRAFT', 'FINAL']),
  createdAt: z.date(),
  downloadUrl: z.string().optional(),
  reportId: z.string().optional(),
  hasPassword: z.boolean().optional(),
  report: z
    .object({
      title: z.string(),
      applicationId: z.string().optional(),
      applicationIds: z.array(z.string()).optional(),
      source: ReportSourceSchema.optional(),
      cadence: ScheduledReportFrequencySchema.nullable().optional(),
      pdfTemplateId: z.string().nullable().optional(),
    })
    .optional(),
});

export const ReportFilesResponseSchema = z.object({
  files: z.array(ReportFileSchema),
});

export const ReportFileUploadResponseSchema = z.object({
  filesCreated: z.number(),
  message: z.string(),
});

export const ReportFilesBodySchema = z.object({
  files: uploadedFilesSchema({
    mimeTypes: ['application/pdf'],
  }).describe('PDF files to upload'),
  password: z
    .string()
    .optional()
    .describe('Optional password for encrypted PDF uploads'),
});

export const ReportFilePasswordResponseSchema = z.object({
  password: z.string(),
});

export const UpdateReportFileStatusBodySchema = z.object({
  status: z.enum(['DRAFT', 'FINAL']),
});

export const UpdateReportFileStatusResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(['DRAFT', 'FINAL']),
  updatedAt: z.date(),
});
